import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Question, PencilSimple, Shield, TreeStructure, GitBranch, Path, IdentificationBadge } from "@phosphor-icons/react";
import { api, apiDelete, apiError } from "@/lib/api";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import PhotoUploader from "@/components/PhotoUploader";
import EmployeePicker from "@/components/EmployeePicker";

/**
 * Available root-cause methodologies. Users can mix multiple in one investigation.
 * Each entry defines the field it maps to on the payload + the empty template.
 */
const METHODOLOGIES = [
  {
    v: "five_whys",
    label: "5 Porqués",
    hint: "Método iterativo para llegar a la causa raíz preguntando ‘¿por qué?’ 5 veces.",
    icon: Question,
    color: "text-blue-700 bg-blue-50 border-blue-200",
  },
  {
    v: "icam",
    label: "ICAM",
    hint: "Incident Cause Analysis Method — estándar de la industria minera (ICMM, BHP).",
    icon: Shield,
    color: "text-amber-800 bg-amber-50 border-amber-200",
  },
  {
    v: "ishikawa",
    label: "Ishikawa (6M)",
    hint: "Diagrama espina de pescado: Mano de obra / Maquinaria / Método / Material / Medición / Medio.",
    icon: GitBranch,
    color: "text-emerald-800 bg-emerald-50 border-emerald-200",
  },
  {
    v: "taproot",
    label: "TapRooT",
    hint: "Metodología estructurada: factores causales → causas genéricas → causas raíz.",
    icon: Path,
    color: "text-purple-800 bg-purple-50 border-purple-200",
  },
];

const ICAM_BUCKETS = [
  { k: "absent_failed_defenses", l: "Barreras ausentes o fallidas" },
  { k: "individual_actions", l: "Acciones del individuo / equipo" },
  { k: "task_environment", l: "Condiciones de tarea / ambiente" },
  { k: "organizational_factors", l: "Factores organizacionales" },
];

const ISHIKAWA_BUCKETS = [
  { k: "personal", l: "Mano de obra (personal)" },
  { k: "maquinaria", l: "Maquinaria / equipos" },
  { k: "metodo", l: "Método / procedimiento" },
  { k: "material", l: "Material / insumos" },
  { k: "medicion", l: "Medición / control" },
  { k: "medio", l: "Medio ambiente / entorno" },
];

const TAPROOT_BUCKETS = [
  { k: "causal_factors", l: "Factores causales" },
  { k: "generic_causes", l: "Causas genéricas" },
  { k: "root_causes", l: "Causas raíz + acción sugerida" },
];

const emptyIcam = () => ({ absent_failed_defenses: [], individual_actions: [], task_environment: [], organizational_factors: [] });
const emptyIshikawa = () => ({ personal: [], maquinaria: [], metodo: [], material: [], medicion: [], medio: [] });
const emptyTaproot = () => ({ causal_factors: [], generic_causes: [], root_causes: [] });

const emptyForm = () => ({
  title: "", date: "", incident_id: "", bowtie_id: "", description: "", project_id: defaultProject(), project_id: defaultProject(),
  immediate_causes: [], root_causes: [],
  methodologies: ["five_whys"],
  five_whys: [{ question: "¿Por qué ocurrió?", answer: "" }],
  icam: emptyIcam(),
  ishikawa: emptyIshikawa(),
  taproot: emptyTaproot(),
  action_plan: [], investigator: "", investigator_employee_id: "",
  participants: [],
  status: "open",
  photos: [],
});

const PARTICIPANT_ROLES = [
  { v: "witness", l: "Testigo" },
  { v: "interviewee", l: "Entrevistado" },
  { v: "committee", l: "Comité" },
  { v: "lead", l: "Líder investigación" },
];

function BucketList({ label, values, onChange, testid, rows = 2, placeholder = "Un ítem por línea" }) {
  return (
    <div>
      <p className="overline-label mb-1">{label}</p>
      <Textarea
        rows={rows}
        placeholder={placeholder}
        value={(values || []).join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n").filter(Boolean))}
        data-testid={testid}
      />
    </div>
  );
}

function MethodologyBadges({ list }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((m) => {
        const meta = METHODOLOGIES.find((x) => x.v === m);
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <span key={m} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${meta.color}`}>
            <Icon size={10} weight="bold" /> {meta.label}
          </span>
        );
      })}
    </div>
  );
}

export default function InvestigationsModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [bowties, setBowties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/hse/investigations").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/hse/bowtie").then((r) => setBowties(r.data)).catch(() => setBowties([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      // Only persist analyses whose methodology is selected — cleaner payload
      const payload = {
        ...form,
        five_whys: form.methodologies.includes("five_whys") ? form.five_whys : [],
        icam: form.methodologies.includes("icam") ? form.icam : null,
        ishikawa: form.methodologies.includes("ishikawa") ? form.ishikawa : null,
        taproot: form.methodologies.includes("taproot") ? form.taproot : null,
      };
      if (!assertProject(form.project_id)) return;
      if (editing) await api.put(`/hse/investigations/${editing}`, payload);
      else await api.post("/hse/investigations", payload);
      toast.success("Investigación guardada");
      setOpen(false); setEditing(null); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = useConfirmDelete();
  const remove = async (id, title = "") => {
    const ok = await confirmDelete({
      title: "¿Eliminar investigación?",
      itemLabel: title,
      description: "El plan de acción y la metodología quedarán en el historial de auditoría con tu motivo.",
    });
    if (!ok) return;
    try { await apiDelete(`/hse/investigations/${id}`, ok.reason); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const toggleMethod = (m) => {
    const has = form.methodologies.includes(m);
    const next = has ? form.methodologies.filter((x) => x !== m) : [...form.methodologies, m];
    // Require at least one methodology
    setForm({ ...form, methodologies: next.length ? next : [m] });
  };

  const addWhy = () => setForm({ ...form, five_whys: [...form.five_whys, { question: `¿Por qué? (nivel ${form.five_whys.length + 1})`, answer: "" }] });
  const addAction = () => setForm({ ...form, action_plan: [...form.action_plan, { description: "", responsible: "", due_date: "", status: "open" }] });
  const addParticipant = () => setForm({ ...form, participants: [...form.participants, { name: "", id_number: "", position: "", role_in_investigation: "witness", employee_id: "" }] });
  const linkParticipant = (idx, emp) => {
    const n = [...form.participants];
    n[idx] = { ...n[idx], employee_id: emp.id, name: emp.name || n[idx].name, id_number: emp.dni || n[idx].id_number, position: emp.position || n[idx].position };
    setForm({ ...form, participants: n });
  };
  const unlinkParticipant = (idx) => {
    const n = [...form.participants];
    n[idx] = { ...n[idx], employee_id: "" };
    setForm({ ...form, participants: n });
  };
  const pickInvestigator = (emp) => setForm({ ...form, investigator: emp.name, investigator_employee_id: emp.id });
  const unlinkInvestigator = () => setForm({ ...form, investigator_employee_id: "" });

  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      ...emptyForm(),
      ...it,
      methodologies: it.methodologies?.length ? it.methodologies : ["five_whys"],
      five_whys: it.five_whys || [],
      icam: it.icam || emptyIcam(),
      ishikawa: it.ishikawa || emptyIshikawa(),
      taproot: it.taproot || emptyTaproot(),
    });
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-600">
          {items.length} investigaciones · {items.filter((x) => x.status === "open").length} abiertas
        </p>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="inv-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={14} weight="bold" /> Nueva investigación</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-4xl max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nueva"} investigación</DialogTitle></DialogHeader>

              <div className="grid grid-cols-2 gap-3">
                <ProjectField className="col-span-2" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="inv-project" />
                <div className="col-span-2"><Label className="overline-label">Título</Label>
                  <Input data-testid="inv-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="overline-label">Fecha</Label>
                  <DateField testid="inv-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
                <div><Label className="overline-label">Investigador</Label>
                  {form.investigator_employee_id ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 h-9" data-testid="inv-investigator-linked">
                      <IdentificationBadge size={14} weight="fill" className="text-emerald-700 shrink-0" />
                      <p className="text-xs font-semibold text-slate-900 flex-1 truncate">{form.investigator}</p>
                      <button onClick={unlinkInvestigator} className="text-[10px] text-slate-500 hover:text-red-600 font-semibold" data-testid="inv-investigator-unlink">Editar</button>
                    </div>
                  ) : (
                    <>
                      <EmployeePicker onPick={pickInvestigator} testid="inv-investigator-picker" placeholder="Buscar en nómina…" />
                      <Input data-testid="inv-investigator" className="mt-1 h-8 text-xs" placeholder="…o escribe manual" value={form.investigator} onChange={(e) => setForm({ ...form, investigator: e.target.value })} />
                    </>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="overline-label">Bow-Tie vinculado (opcional)</Label>
                  <select
                    data-testid="inv-bowtie-id"
                    className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
                    value={form.bowtie_id || ""}
                    onChange={(e) => setForm({ ...form, bowtie_id: e.target.value })}
                  >
                    <option value="">— Sin vínculo —</option>
                    {bowties.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.top_event} {b.hazard ? `— ${b.hazard}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Si este incidente activó una consecuencia de un Bow-Tie previamente registrado, vincúlalo aquí para trazabilidad.
                  </p>
                </div>
                <div className="col-span-2"><Label className="overline-label">Descripción del evento</Label>
                  <Textarea rows={2} data-testid="inv-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

                {/* Methodology selector */}
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <p className="overline-label mb-2">Metodologías de análisis (selecciona una o varias)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {METHODOLOGIES.map((m) => {
                      const on = form.methodologies.includes(m.v);
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.v}
                          type="button"
                          onClick={() => toggleMethod(m.v)}
                          data-testid={`inv-method-${m.v}`}
                          title={m.hint}
                          className={`text-left p-2 rounded-md border-2 transition-colors ${on ? m.color + " ring-2 ring-blue-500" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
                        >
                          <div className="flex items-center gap-1.5 text-xs font-bold">
                            <Icon size={14} weight="bold" /> {m.label}
                          </div>
                          <p className="text-[10px] mt-0.5 opacity-80 leading-tight">{m.hint}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <BucketList label="Causas inmediatas (actos y condiciones sub-estándar)" values={form.immediate_causes} onChange={(v) => setForm({ ...form, immediate_causes: v })} testid="inv-immediate-causes" />
                </div>
                <div className="col-span-2">
                  <BucketList label="Causas básicas / raíz (factores personales y del trabajo)" values={form.root_causes} onChange={(v) => setForm({ ...form, root_causes: v })} testid="inv-root-causes" />
                </div>

                {/* 5 Whys section */}
                {form.methodologies.includes("five_whys") && (
                  <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="inv-section-five_whys">
                    <div className="flex items-center justify-between mb-2">
                      <p className="overline-label flex items-center gap-1"><Question size={12} weight="bold" className="text-blue-700" /> Cinco Por Qués</p>
                      <Button size="sm" variant="ghost" onClick={addWhy} data-testid="inv-add-why" className="h-7 text-xs text-blue-900"><Plus size={12} /> Añadir</Button>
                    </div>
                    <div className="space-y-2">
                      {form.five_whys.map((w, idx) => (
                        <div key={idx} className="bg-blue-50/40 border border-blue-100 rounded p-2">
                          <div className="flex items-center gap-1 mb-1">
                            <Question size={14} className="text-blue-900" />
                            <Input className="h-7 text-xs font-semibold flex-1" value={w.question} onChange={(e) => { const n=[...form.five_whys]; n[idx]={...n[idx],question:e.target.value}; setForm({...form,five_whys:n}); }} data-testid={`inv-why-q-${idx}`} />
                            <button onClick={() => setForm({ ...form, five_whys: form.five_whys.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
                          </div>
                          <Textarea rows={2} className="text-xs" placeholder="Respuesta" value={w.answer} onChange={(e) => { const n=[...form.five_whys]; n[idx]={...n[idx],answer:e.target.value}; setForm({...form,five_whys:n}); }} data-testid={`inv-why-a-${idx}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ICAM section */}
                {form.methodologies.includes("icam") && (
                  <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="inv-section-icam">
                    <p className="overline-label mb-2 flex items-center gap-1"><Shield size={12} weight="bold" className="text-amber-700" /> ICAM · 4 niveles de análisis</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {ICAM_BUCKETS.map((b) => (
                        <BucketList
                          key={b.k}
                          label={b.l}
                          values={form.icam?.[b.k] || []}
                          onChange={(v) => setForm({ ...form, icam: { ...form.icam, [b.k]: v } })}
                          testid={`inv-icam-${b.k}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Ishikawa section */}
                {form.methodologies.includes("ishikawa") && (
                  <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="inv-section-ishikawa">
                    <p className="overline-label mb-2 flex items-center gap-1"><GitBranch size={12} weight="bold" className="text-emerald-700" /> Ishikawa · 6M</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {ISHIKAWA_BUCKETS.map((b) => (
                        <BucketList
                          key={b.k}
                          label={b.l}
                          values={form.ishikawa?.[b.k] || []}
                          onChange={(v) => setForm({ ...form, ishikawa: { ...form.ishikawa, [b.k]: v } })}
                          testid={`inv-ishikawa-${b.k}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* TapRooT section */}
                {form.methodologies.includes("taproot") && (
                  <div className="col-span-2 border-t border-slate-100 pt-3" data-testid="inv-section-taproot">
                    <p className="overline-label mb-2 flex items-center gap-1"><Path size={12} weight="bold" className="text-purple-700" /> TapRooT · Factores → Causas → Raíz</p>
                    <div className="grid md:grid-cols-3 gap-3">
                      {TAPROOT_BUCKETS.map((b) => (
                        <BucketList
                          key={b.k}
                          label={b.l}
                          values={form.taproot?.[b.k] || []}
                          onChange={(v) => setForm({ ...form, taproot: { ...form.taproot, [b.k]: v } })}
                          testid={`inv-taproot-${b.k}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline-label">Participantes ({form.participants.length}) — testigos, entrevistados, comité</p>
                    <Button size="sm" variant="ghost" onClick={addParticipant} data-testid="inv-add-participant" className="h-7 text-xs text-blue-900"><Plus size={12} /> Añadir</Button>
                  </div>
                  <p className="text-[10px] text-slate-500 mb-1 italic">Vincula a nómina para trazabilidad HSE completa. Externos pueden escribirse manualmente.</p>
                  <div className="space-y-1.5">
                    {form.participants.map((p, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-1 items-start" data-testid={`inv-part-row-${idx}`}>
                        {p.employee_id ? (
                          <div className="col-span-9 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid={`inv-part-linked-${idx}`}>
                            <IdentificationBadge size={16} weight="fill" className="text-emerald-700 shrink-0" />
                            <div className="flex-1 min-w-0 text-xs">
                              <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                              <p className="text-[10px] text-slate-500 truncate">DNI {p.id_number || "-"} · {p.position || "-"} · <span className="text-emerald-700 font-bold">Nómina</span></p>
                            </div>
                            <button onClick={() => unlinkParticipant(idx)} className="text-[10px] text-slate-500 hover:text-red-600 font-semibold" data-testid={`inv-part-unlink-${idx}`}>Editar</button>
                          </div>
                        ) : (
                          <>
                            <div className="col-span-4"><EmployeePicker onPick={(emp) => linkParticipant(idx, emp)} testid={`inv-part-picker-${idx}`} placeholder="Buscar nómina o →" /></div>
                            <Input className="col-span-3 h-8 text-xs" placeholder="Nombre manual" value={p.name} onChange={(e) => { const n=[...form.participants]; n[idx]={...n[idx],name:e.target.value}; setForm({...form,participants:n}); }} data-testid={`inv-part-name-${idx}`} />
                            <Input className="col-span-2 h-8 text-xs" placeholder="DNI" value={p.id_number} onChange={(e) => { const n=[...form.participants]; n[idx]={...n[idx],id_number:e.target.value}; setForm({...form,participants:n}); }} />
                          </>
                        )}
                        <select className="col-span-2 h-8 text-xs border border-slate-200 rounded px-1 bg-white" value={p.role_in_investigation} onChange={(e) => { const n=[...form.participants]; n[idx]={...n[idx],role_in_investigation:e.target.value}; setForm({...form,participants:n}); }} data-testid={`inv-part-role-${idx}`}>
                          {PARTICIPANT_ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                        </select>
                        <button className="col-span-1 h-8 text-slate-400 hover:text-red-600 flex items-center justify-center" onClick={() => setForm({ ...form, participants: form.participants.filter((_, i) => i !== idx) })} data-testid={`inv-part-remove-${idx}`}><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline-label flex items-center gap-1"><TreeStructure size={12} weight="bold" /> Plan de acción</p>
                    <Button size="sm" variant="ghost" onClick={addAction} data-testid="inv-add-action" className="h-7 text-xs text-blue-900"><Plus size={12} /> Añadir</Button>
                  </div>
                  <div className="space-y-1">
                    {form.action_plan.map((a, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-1">
                        <Input className="col-span-6 h-8 text-xs" placeholder="Acción" value={a.description} onChange={(e) => { const n=[...form.action_plan]; n[idx]={...n[idx],description:e.target.value}; setForm({...form,action_plan:n}); }} />
                        <Input className="col-span-3 h-8 text-xs" placeholder="Responsable" value={a.responsible} onChange={(e) => { const n=[...form.action_plan]; n[idx]={...n[idx],responsible:e.target.value}; setForm({...form,action_plan:n}); }} />
                        <Input className="col-span-2 h-8 text-xs" type="date" value={a.due_date} onChange={(e) => { const n=[...form.action_plan]; n[idx]={...n[idx],due_date:e.target.value}; setForm({...form,action_plan:n}); }} />
                        <button className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => setForm({ ...form, action_plan: form.action_plan.filter((_, i) => i !== idx) })}><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <p className="overline-label mb-2">Evidencia fotográfica (lugar, daños, testigos, croquis)</p>
                  <PhotoUploader
                    value={form.photos || []}
                    onChange={(photos) => setForm({ ...form, photos })}
                    max={8}
                    testidPrefix="inv-photo"
                  />
                </div>
              </div>
              <Button data-testid="inv-save" onClick={save} disabled={!form.title || !form.date} className="bg-blue-900 hover:bg-blue-800 w-full mt-2">
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-lg">Sin investigaciones</p>}
        {items.map((it) => (
          <div key={it.id} data-testid={`inv-card-${it.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2 gap-3">
              <div>
                <p className="font-heading font-bold text-slate-900">{it.title}</p>
                <p className="text-xs text-slate-500">{it.date} · {it.investigator}</p>
                <div className="mt-1"><MethodologyBadges list={it.methodologies || (it.five_whys?.length ? ["five_whys"] : [])} /></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${it.status === "closed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{it.status}</span>
                {canWrite && (
                  <>
                    <button onClick={() => openEdit(it)} className="text-slate-400 hover:text-blue-900"><PencilSimple size={14} /></button>
                    <button data-testid={`inv-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                  </>
                )}
              </div>
            </div>
            {it.description && <p className="text-xs text-slate-700 mb-2">{it.description}</p>}
            {(it.photos || []).length > 0 && (
              <div className="flex gap-1 mb-2" data-testid={`inv-photos-${it.id}`}>
                {(it.photos || []).slice(0, 6).map((src, i) => (
                  <img key={i} src={src} alt="" className="w-14 h-14 rounded object-cover border border-slate-200" />
                ))}
                {(it.photos || []).length > 6 && (
                  <span className="text-[10px] text-slate-500 self-center ml-1">+{(it.photos || []).length - 6}</span>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div><p className="overline-label mb-1">Causas inmediatas</p><ul className="space-y-0.5">{(it.immediate_causes || []).map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
              <div><p className="overline-label mb-1">Causas raíz</p><ul className="space-y-0.5">{(it.root_causes || []).map((c, i) => <li key={i}>• {c}</li>)}</ul></div>

              {(it.methodologies || []).includes("five_whys") && (it.five_whys || []).length > 0 && (
                <div className="md:col-span-2 bg-blue-50/30 rounded p-2 border border-blue-100">
                  <p className="overline-label mb-1 text-blue-900">5 Por Qués</p>
                  <ol className="space-y-1 pl-3 border-l-2 border-blue-200">
                    {(it.five_whys || []).map((w, i) => (
                      <li key={i}><span className="font-semibold">{w.question}</span> <span className="text-slate-600">{w.answer}</span></li>
                    ))}
                  </ol>
                </div>
              )}

              {(it.methodologies || []).includes("icam") && it.icam && (
                <div className="md:col-span-2 bg-amber-50/40 rounded p-2 border border-amber-100">
                  <p className="overline-label mb-1 text-amber-900">ICAM</p>
                  <div className="grid md:grid-cols-2 gap-2">
                    {ICAM_BUCKETS.map((b) => (
                      <div key={b.k}>
                        <p className="font-semibold text-[11px] text-slate-700">{b.l}</p>
                        <ul>{(it.icam[b.k] || []).map((v, i) => <li key={i}>• {v}</li>)}</ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(it.methodologies || []).includes("ishikawa") && it.ishikawa && (
                <div className="md:col-span-2 bg-emerald-50/40 rounded p-2 border border-emerald-100">
                  <p className="overline-label mb-1 text-emerald-900">Ishikawa (6M)</p>
                  <div className="grid md:grid-cols-3 gap-2">
                    {ISHIKAWA_BUCKETS.map((b) => (
                      <div key={b.k}>
                        <p className="font-semibold text-[11px] text-slate-700">{b.l}</p>
                        <ul>{(it.ishikawa[b.k] || []).map((v, i) => <li key={i}>• {v}</li>)}</ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(it.methodologies || []).includes("taproot") && it.taproot && (
                <div className="md:col-span-2 bg-purple-50/40 rounded p-2 border border-purple-100">
                  <p className="overline-label mb-1 text-purple-900">TapRooT</p>
                  <div className="grid md:grid-cols-3 gap-2">
                    {TAPROOT_BUCKETS.map((b) => (
                      <div key={b.k}>
                        <p className="font-semibold text-[11px] text-slate-700">{b.l}</p>
                        <ul>{(it.taproot[b.k] || []).map((v, i) => <li key={i}>• {v}</li>)}</ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <p className="overline-label mb-1">Plan de acción ({(it.action_plan || []).length})</p>
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left">Acción</th><th className="text-left">Responsable</th><th className="text-left">Fecha</th><th>Estado</th></tr></thead>
                  <tbody>{(it.action_plan || []).map((a, i) => (
                    <tr key={i} className="border-t border-slate-100"><td className="py-1">{a.description}</td><td>{a.responsible}</td><td className="font-mono">{a.due_date}</td><td className="text-center"><span className="text-[10px] uppercase font-bold text-slate-500">{a.status}</span></td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
